import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SkipForward, Trash2, Check, ExternalLink, AlertTriangle, Upload, FileSpreadsheet, CheckCircle2, Inbox, Copy } from 'lucide-react';
import { documentAPI, documentLinesAPI } from '../../lib/api';
import { useTenant } from '../../contexts/TenantContext';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BilagViewer } from '../../components/documents/BilagViewer';
import { isFieldUncertain, fieldInputClass, fieldLabelClass, vatContradiction } from '../../components/documents/ConfidenceField';
import { toast } from 'sonner';

// Map a posting-line field to the extracted_data key its confidence is recorded under.
const CONFIDENCE_KEY = {
    supplier: 'supplier_name',
    date: 'invoice_date',
    amount: 'total_amount',
    vat_amount: 'vat_amount',
    account: 'account_code',
    vat_code: 'vat_code',
};

const DOC_TYPES = [
    { value: 'purchase', label: 'Købsbilag' },
    { value: 'sale', label: 'Salgsbilag' },
    { value: 'bank_statement', label: 'Bankkontoudtog' },
];

export default function ReviewScreenPage() {
    const { currentTenant } = useTenant();

    const [queue, setQueue] = useState([]);
    const [idx, setIdx] = useState(0);
    const [loadingQueue, setLoadingQueue] = useState(true);
    // Counts approvals made in THIS sitting — drives the header badge and the finished-queue state.
    const [sessionApproved, setSessionApproved] = useState(0);
    const [queueMeta, setQueueMeta] = useState({ documentsTotal: null, inboundCode: null });
    // Documents set aside this sitting. Session state ONLY — nothing is written to the document,
    // its status is untouched, and it stays in the full list. Cleared on a deliberate reload
    // (the component remounts) or once everything else in the queue is done.
    const [skipped, setSkipped] = useState(() => new Set());

    const [doc, setDoc] = useState(null);           // getOne payload (extracted_data, confidence)
    const [line, setLine] = useState(null);         // the single posting line being edited
    const [lineMeta, setLineMeta] = useState(null); // getLines payload (supplier, totals, type)
    const [bankMatch, setBankMatch] = useState(null);
    const [loadingDoc, setLoadingDoc] = useState(false);

    const [chart, setChart] = useState({ accounts: [], source: null, note: null });
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);

    const visibleQueue = queue.filter((d) => !skipped.has(d.id));
    const current = visibleQueue[idx] || null;
    const total = visibleQueue.length;
    const skippedCount = queue.reduce((n, d) => n + (skipped.has(d.id) ? 1 : 0), 0);

    // ── Queue ────────────────────────────────────────────────────────────────
    const loadQueue = useCallback(async () => {
        if (!currentTenant) return;
        setLoadingQueue(true);
        try {
            const r = await documentLinesAPI.reviewQueue(currentTenant.id);
            setQueue(r.data?.queue || []);
            setSkipped(new Set());
            setQueueMeta({
                documentsTotal: r.data?.documents_total ?? null,
                inboundCode: r.data?.inbound_code ?? null,
            });
            setIdx(0);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke hente bilagskøen');
        } finally {
            setLoadingQueue(false);
        }
    }, [currentTenant]);

    useEffect(() => { loadQueue(); }, [loadQueue]);

    // Queue exhausted but documents were set aside -> offer them again rather than reporting
    // "all done" when work remains.
    useEffect(() => {
        if (!loadingQueue && total === 0 && skipped.size > 0) {
            setSkipped(new Set());
            setIdx(0);
        }
    }, [loadingQueue, total, skipped.size]);

    // ── Client chart of accounts: fetched once per client, cached server-side too ──
    useEffect(() => {
        if (!currentTenant) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await documentLinesAPI.clientChart(currentTenant.id);
                if (!cancelled) setChart({
                    accounts: r.data?.accounts || [],
                    source: r.data?.source,
                    note: r.data?.note,
                    reason: r.data?.reason,
                });
            } catch {
                if (!cancelled) setChart({
                    accounts: [], source: 'error',
                    note: 'Kontoplanen kunne ikke hentes — vælg kontoen manuelt.',
                });
            }
        })();
        return () => { cancelled = true; };
    }, [currentTenant]);

    // ── Current document ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!current || !current.supported) { setDoc(null); setLine(null); return; }
        let cancelled = false;
        setLoadingDoc(true);
        (async () => {
            try {
                const [dRes, lRes, bRes] = await Promise.all([
                    documentAPI.getOne(current.id),
                    documentLinesAPI.getLines(current.id),
                    documentLinesAPI.bankMatch(current.id).catch(() => ({ data: { matched: false } })),
                ]);
                if (cancelled) return;
                setDoc(dRes.data);
                setLineMeta(lRes.data);
                setLine((lRes.data?.lines || [])[0] || null);
                setBankMatch(bRes.data);
            } catch (e) {
                if (!cancelled) toast.error(e.response?.data?.detail || 'Kunne ikke hente bilaget');
            } finally {
                if (!cancelled) setLoadingDoc(false);
            }
        })();
        return () => { cancelled = true; };
    }, [current]);

    // ── Uncertainty (shared with the documents modal) ────────────────────────
    const isUncertain = (field) => isFieldUncertain(doc, CONFIDENCE_KEY[field]);
    const fieldCls = (field) => fieldInputClass(isUncertain(field));
    const labelCls = (field) => fieldLabelClass(isUncertain(field));

    const setField = (k) => (v) => setLine((l) => ({ ...l, [k]: v }));

    // ── Actions ──────────────────────────────────────────────────────────────
    // Set the current document aside for this sitting. Removing it from the visible queue means
    // the next document slides into the same index, so idx only needs clamping.
    const advance = useCallback(() => {
        if (!current) return;
        const id = current.id;
        setSkipped((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
        });
        setIdx((i) => Math.max(0, Math.min(i, total - 2)));
    }, [current, total]);

    // Sale vs purchase decides the account side and whether the VAT is input or output. While it
    // is only the intake hint, approving would book a guess — so the action is blocked, not warned.
    const typeUnconfirmed = !!current?.needs_type_confirmation;

    const approve = useCallback(async () => {
        if (!current || !current.supported || !line || busyRef.current) return;
        if (current.needs_type_confirmation) {
            toast.error('Bekræft bilagstypen (salg eller køb) før du godkender.');
            return;
        }
        busyRef.current = true; setBusy(true);
        try {
            await documentLinesAPI.saveLines(current.id, [line]);
            await documentLinesAPI.approveDocument(current.id, 'single_screen');
            toast.success('Godkendt');
            setSessionApproved((n) => n + 1);
            setQueue((q) => q.filter((x) => x.id !== current.id));
            setIdx((i) => Math.max(0, Math.min(i, total - 2)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke godkende bilaget');
        } finally {
            busyRef.current = false; setBusy(false);
        }
    }, [current, line, total]);

    const reject = useCallback(async () => {
        if (!current || busyRef.current) return;
        if (!window.confirm(`Afvis og slet "${current.filename}"? Handlingen kan ikke fortrydes.`)) return;
        busyRef.current = true; setBusy(true);
        try {
            await documentAPI.deleteDocument(current.id);
            toast.success('Bilag afvist');
            setQueue((q) => q.filter((x) => x.id !== current.id));
            setIdx((i) => Math.max(0, Math.min(i, total - 2)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke afvise bilaget');
        } finally {
            busyRef.current = false; setBusy(false);
        }
    }, [current, total]);

    // "Behold som selvstændigt bilag" — clears the flag only; nothing else is touched.
    const keepAsSeparate = useCallback(async () => {
        if (!current || busyRef.current) return;
        busyRef.current = true; setBusy(true);
        try {
            await documentLinesAPI.clearDuplicateFlag(current.id);
            toast.success('Beholdt som selvstændigt bilag');
            setQueue((q) => q.map((x) => x.id === current.id
                ? { ...x, is_duplicate: false, supported: true, block_reason: null, block_kind: null, duplicate_of: null }
                : x));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke opdatere bilaget');
        } finally {
            busyRef.current = false; setBusy(false);
        }
    }, [current]);

    // "Afvis som dublet" — the same explicit, confirmed removal as the normal reject action.
    const rejectAsDuplicate = useCallback(async () => {
        if (!current || busyRef.current) return;
        if (!window.confirm(
            `Afvis "${current.filename}" som dublet? Bilaget slettes og kan ikke gendannes.`)) return;
        busyRef.current = true; setBusy(true);
        try {
            await documentAPI.deleteDocument(current.id);
            toast.success('Afvist som dublet');
            setQueue((q) => q.filter((x) => x.id !== current.id));
            setIdx((i) => Math.max(0, Math.min(i, total - 2)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke afvise bilaget');
        } finally {
            busyRef.current = false; setBusy(false);
        }
    }, [current, total]);

    const setDocType = async (value) => {
        if (!current) return;
        try {
            await documentLinesAPI.setDocumentType(current.id, value);
            const lRes = await documentLinesAPI.getLines(current.id);
            setLineMeta(lRes.data);
            setLine((lRes.data?.lines || [])[0] || null);
            setQueue((q) => q.map((x) => x.id === current.id ? { ...x, needs_type_confirmation: false } : x));
            toast.success('Bilagstype opdateret');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke opdatere bilagstypen');
        }
    };

    // ── Keyboard: Enter approves, Esc skips — without focus in a field ───────
    useEffect(() => {
        const onKey = (e) => {
            const el = e.target;
            const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA'
                || el.tagName === 'SELECT' || el.isContentEditable
                || el.getAttribute?.('role') === 'combobox');
            if (typing) return;
            if (e.key === 'Enter') { e.preventDefault(); approve(); }   // approve() itself refuses
                                                                        // while the type is unconfirmed
            else if (e.key === 'Escape') { e.preventDefault(); advance(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [approve, advance]);

    // ── Render ───────────────────────────────────────────────────────────────
    if (!currentTenant) {
        return <div className="p-8 text-center text-muted-foreground">Vælg en virksomhed for at gennemgå bilag.</div>;
    }
    if (loadingQueue) {
        return <div className="p-8 text-center text-muted-foreground">Henter bilag…</div>;
    }
    if (total === 0) {
        // CASE A — the accountant just worked through a queue (or the client has documents that
        // are all reviewed). CASE B — this client has no documents at all yet.
        const finishedAQueue = sessionApproved > 0 || (queueMeta.documentsTotal ?? 0) > 0;
        const inboundAddress = queueMeta.inboundCode ? `${queueMeta.inboundCode}@bilag.accountrix.dk` : null;

        return (
            <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="review-empty-state">
                {finishedAQueue ? (
                    <>
                        <CheckCircle2 className="w-10 h-10 text-[#0F766E] mx-auto mb-4" />
                        <h2 className="font-heading text-2xl font-bold" data-testid="empty-done-heading">
                            Alle bilag er gennemgået
                        </h2>
                        <p className="mt-2 text-muted-foreground" data-testid="empty-approved-count">
                            {sessionApproved} bilag godkendt
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                            <Link to="/app/bank/export">
                                <Button size="lg" className="w-full sm:w-auto" data-testid="empty-to-export">
                                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Gå til levering
                                </Button>
                            </Link>
                            <Link to="/app/documents/all?upload=1">
                                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="empty-upload-more">
                                    <Upload className="w-4 h-4 mr-2" /> Upload flere bilag
                                </Button>
                            </Link>
                        </div>
                    </>
                ) : (
                    <>
                        <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                        <h2 className="font-heading text-2xl font-bold" data-testid="empty-none-heading">
                            Ingen bilag endnu
                        </h2>
                        <p className="mt-2 text-muted-foreground">
                            Upload bilag her, eller lad kunden sende dem til
                            {inboundAddress ? ' ' : ' virksomhedens '}
                            {inboundAddress
                                ? <span className="font-mono text-foreground">{inboundAddress}</span>
                                : 'indbakke-adresse'}
                            .
                        </p>
                        <div className="mt-8">
                            <Link to="/app/documents/all?upload=1">
                                <Button size="lg" data-testid="empty-upload">
                                    <Upload className="w-4 h-4 mr-2" /> Upload bilag
                                </Button>
                            </Link>
                        </div>
                    </>
                )}

                <div className="mt-8 flex flex-col gap-2 items-center">
                    <Link to="/app/documents/all" className="text-sm text-primary hover:underline"
                          data-testid="empty-full-list">
                        Se alle bilag
                    </Link>
                    <Link to="/app/documents/all"
                          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                          data-testid="empty-old-view">
                        Skift til den gamle bilagsvisning <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
            </div>
        );
    }

    const pct = total ? Math.round(((idx) / total) * 100) : 0;

    return (
        <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="review-screen">
            {/* Header: position + progress */}
            <div className="px-6 py-3 border-b bg-background">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium" data-testid="review-counter">
                            Bilag {Math.min(idx + 1, total)} af {total}
                        </span>
                        {skippedCount > 0 && (
                            <span className="text-xs text-muted-foreground inline-flex items-center gap-1"
                                  data-testid="session-skipped">
                                <SkipForward className="w-3.5 h-3.5" /> {skippedCount} sprunget over
                            </span>
                        )}
                        {sessionApproved > 0 && (
                            <span className="text-xs text-[#0F766E] font-medium inline-flex items-center gap-1"
                                  data-testid="session-approved">
                                <Check className="w-3.5 h-3.5" /> {sessionApproved} godkendt
                            </span>
                        )}
                    </div>
                    <Link to="/app/documents/all"
                          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                          data-testid="review-old-flow-link">
                        Skift til den gamle bilagsvisning <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                </div>
            </div>

            <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0">
                {/* LEFT — the bilag, always visible. Shared viewer: our own PDF canvas, so the
                    browser's PDF toolbar and thumbnail rail never appear. */}
                <div className="border-r flex flex-col min-h-0">
                    {current?.supported && (
                        <BilagViewer docId={current.id} filename={current.filename}
                                     fileType={current.file_type} className="flex-1" />
                    )}
                </div>

                {/* RIGHT — the finished posting line */}
                <div className="flex flex-col min-h-0">
                    <div className="flex-1 overflow-auto p-6 space-y-4">
                        {!current?.supported ? (
                            /* A state the single screen cannot represent: explain, link, never dead-end. */
                            <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 space-y-3"
                                 data-testid={current?.block_kind === 'duplicate' ? 'review-duplicate' : 'review-blocked'}>
                                <div className="flex items-start gap-2">
                                    {current?.block_kind === 'duplicate'
                                        ? <Copy className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                        : <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                                    <div>
                                        <p className="font-medium text-amber-800">
                                            {current?.block_kind === 'duplicate'
                                                ? 'Dette bilag kan ikke godkendes'
                                                : 'Dette bilag kan ikke gennemgås her'}
                                        </p>
                                        <p className="text-sm text-amber-700 mt-1">{current?.block_reason}</p>
                                    </div>
                                </div>

                                {current?.block_kind === 'duplicate' ? (
                                    <>
                                        {current?.duplicate_of && (
                                            <Link to={`/app/documents/${current.duplicate_of.id}/review`}
                                                  className="inline-flex items-center gap-1 text-sm font-medium text-amber-900 underline"
                                                  data-testid="duplicate-original-link">
                                                Åbn originalen ({current.duplicate_of.status}) <ExternalLink className="w-3.5 h-3.5" />
                                            </Link>
                                        )}
                                        <div className="flex flex-wrap items-center gap-2 pt-1">
                                            <Button size="sm" variant="outline" onClick={keepAsSeparate} disabled={busy}
                                                    data-testid="duplicate-keep">
                                                Behold som selvstændigt bilag
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={rejectAsDuplicate} disabled={busy}
                                                    className="text-destructive hover:text-destructive"
                                                    data-testid="duplicate-reject">
                                                <Trash2 className="w-4 h-4 mr-1.5" /> Afvis som dublet
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={advance} disabled={busy}>
                                                Spring over
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Link to={current?.old_review_url || '/app/documents/all'}
                                              className="inline-flex items-center gap-1 text-sm font-medium text-amber-900 underline"
                                              data-testid="review-blocked-link">
                                            Åbn i den gamle visning <ExternalLink className="w-3.5 h-3.5" />
                                        </Link>
                                        <div>
                                            <Button size="sm" variant="outline" onClick={advance}>Spring over</Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : loadingDoc || !line ? (
                            <p className="text-sm text-muted-foreground">Henter posteringslinje…</p>
                        ) : (
                            <>
                                {/* Bank match — passive status only, never a step, never blocks approval */}
                                {bankMatch?.matched ? (
                                    <div className="inline-flex items-center gap-2 rounded-full border border-green-300 bg-green-50 px-3 py-1"
                                         data-testid="bank-chip-matched">
                                        <span className="text-xs font-medium text-green-700">
                                            Bankpostering fundet · {bankMatch.date} · {Number(bankMatch.amount_signed).toLocaleString('da-DK', { minimumFractionDigits: 2 })} {bankMatch.currency || 'DKK'}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1"
                                         data-testid="bank-chip-unmatched">
                                        <span className="text-xs font-medium text-amber-700">Ingen bankpostering fundet</span>
                                    </div>
                                )}

                                {/* Document type — only while unconfirmed, so the queue stays usable */}
                                {current?.needs_type_confirmation && (
                                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                                        <Label className="text-xs text-amber-700 font-medium">
                                            Bekræft bilagstype — påkrævet før godkendelse
                                        </Label>
                                        <Select value={lineMeta?.document_type || ''} onValueChange={setDocType}>
                                            <SelectTrigger className="h-9 mt-1 bg-background" data-testid="doc-type">
                                                <SelectValue placeholder="Vælg type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {DOC_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}

                                {/* What was done to this bilag and on what stored evidence —
                                    composed server-side from `documents.source` and the vendor
                                    pattern that actually drove the coding. No AI call. Sits above
                                    the fields because it is the basis for the values in them. */}
                                {lineMeta?.provenance && (
                                    <p className="text-xs text-muted-foreground mb-3 leading-relaxed"
                                       data-testid="review-provenance">
                                        {lineMeta.provenance}
                                    </p>
                                )}

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label className={labelCls('supplier')}>Leverandør</Label>
                                        <input className={fieldCls('supplier')} value={lineMeta?.supplier || ''} readOnly
                                               data-testid="f-supplier" />
                                    </div>
                                    <div>
                                        <Label className={labelCls('date')}>Dato</Label>
                                        <input className={fieldCls('date')} value={line.date || ''}
                                               onChange={(e) => setField('date')(e.target.value)} data-testid="f-date" />
                                    </div>
                                    <div>
                                        <Label className={labelCls('amount')}>Beløb</Label>
                                        <input className={fieldCls('amount')} type="number" step="0.01" value={line.amount ?? ''}
                                               onChange={(e) => setField('amount')(parseFloat(e.target.value))} data-testid="f-amount" />
                                    </div>
                                    <div>
                                        <Label className={labelCls('account')}>Konto</Label>
                                        <Select value={String(line.account || '')} onValueChange={setField('account')}>
                                            <SelectTrigger className={fieldCls('account')} data-testid="f-account">
                                                <SelectValue placeholder="Vælg konto" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-72">
                                                {chart.accounts.map((a) => (
                                                    <SelectItem key={String(a.konto)} value={String(a.konto)}>
                                                        {a.konto} — {a.navn}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className={labelCls('contra')}>Modkonto</Label>
                                        <input className={fieldCls('contra')} value={line.contra_account || ''}
                                               onChange={(e) => setField('contra_account')(e.target.value)} data-testid="f-contra" />
                                    </div>
                                    <div>
                                        <Label className={labelCls('vat_code')}>Momskode</Label>
                                        <input className={fieldCls('vat_code')} value={line.vat_code || ''}
                                               onChange={(e) => setField('vat_code')(e.target.value)} data-testid="f-vat-code" />
                                    </div>
                                    <div>
                                        <Label className={labelCls('vat_amount')}>Momsbeløb</Label>
                                        <input className={fieldCls('vat_amount')} type="number" step="0.01"
                                               value={(doc?.extracted_data || {}).vat_amount ?? ''} readOnly
                                               data-testid="f-vat-amount" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className={labelCls('text')}>Tekst</Label>
                                        <input className={fieldCls('text')} value={line.text || ''}
                                               onChange={(e) => setField('text')(e.target.value)} data-testid="f-text" />
                                    </div>
                                </div>

                                {vatContradiction(line.vat_code, (doc?.extracted_data || {}).vat_amount) && (
                                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3"
                                         data-testid="vat-contradiction">
                                        <p className="text-[11px] text-amber-700">
                                            {vatContradiction(line.vat_code, (doc?.extracted_data || {}).vat_amount)}
                                            {' '}Kontroller momskoden og momsbeløbet, før du godkender.
                                        </p>
                                    </div>
                                )}

                                {chart.note && (
                                    <p className="text-[11px] text-amber-600" data-testid="chart-note">
                                        {chart.note}
                                        {['reconnect_required', 'not_connected'].includes(chart.reason) && (
                                            <>
                                                {' '}
                                                <Link to="/app/settings" className="underline font-medium"
                                                      data-testid="chart-reconnect-link">
                                                    Åbn Indstillinger
                                                </Link>
                                            </>
                                        )}
                                    </p>
                                )}
                                {chart.source && chart.source !== 'fallback' && chart.source !== 'error' && (
                                    <p className="text-[11px] text-muted-foreground">
                                        Kontoplan hentet fra kundens system ({chart.source}).
                                    </p>
                                )}
                            </>
                        )}
                    </div>

                    {/* Actions */}
                    {current?.supported && (
                        <div className="border-t p-4 flex items-center gap-2 bg-background">
                            <Button onClick={approve} disabled={busy || !line || typeUnconfirmed}
                                    className="px-6"
                                    title={typeUnconfirmed ? 'Bekræft bilagstypen først' : undefined}
                                    data-testid="btn-approve">
                                <Check className="w-4 h-4 mr-2" /> Godkend &amp; næste
                            </Button>
                            <Button variant="outline" onClick={advance} disabled={busy} data-testid="btn-skip">
                                <SkipForward className="w-4 h-4 mr-2" /> Spring over
                            </Button>
                            <Button variant="ghost" onClick={reject} disabled={busy}
                                    className="text-destructive hover:text-destructive" data-testid="btn-reject">
                                <Trash2 className="w-4 h-4 mr-2" /> Afvis
                            </Button>
                            <span className="ml-auto text-[11px] text-muted-foreground">
                                Enter = godkend · Esc = spring over
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
